import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import cloneDeep from 'lodash/cloneDeep';

import ModalForm from '../modalForm';
import DevicesTable from '../devicesTable';
import BusinessPlansTable from '../businessPlansTable';
import { FieldWrapper } from './css';
import { Button } from '@telus-uds/components-web';
import ErrorMessage from '../../errorMessage';

import {
  keyDevices,
  keySpareDevices,
  keyDevicesWereChanged,
  keyBusinessAppPlans,
  fieldAvailableDiscountsAndPromotions
} from '../../utils/constant';
import { useLocalizedStringsContext } from '../../context/LocalizedStringsContext';
import { checkPrimaryDeviceLimit, checkSpareDeviceLimit, checkSpareDeviceAvailability } from '../utils/deviceFiltering';

const FieldButton = ({ data, orderData }) => {
  const {
    name,
    label,
    readonly,
    formFields,
    setFormFields,
    modalForms,
    dueNow,
    setDueNow,
    dueMonthly,
    setDueMonthly,
    setIsFormTouched,
    setIsFormValidated,
    isOrderFulfillmentTeam,
    isDevicesPromo
  } = data;
  const localizedStrings = useLocalizedStringsContext();
  const {
    contentfulValues: { valuePaymentUpfront },
    modalForm: { modalFormDevices }
  } = localizedStrings;
  const [isModalOpened, setIsModalOpened] = useState(false);
  const [spareDeviceCheck, setSpareDeviceCheck] = useState(null);
  const [isCheckingSpareDevices, setIsCheckingSpareDevices] = useState(false);
  
  // Check spare device availability when component mounts or formFields change (skip for BA orders)
  useEffect(() => {
    const isSpareDevices = label !== modalFormDevices;
    const isBAOrder = orderData && orderData.type === 'BA';
    
    if (isSpareDevices && orderData && formFields && !isBAOrder) {
      setIsCheckingSpareDevices(true);
      checkSpareDeviceAvailability(formFields, orderData)
        .then(result => {
          setSpareDeviceCheck(result);
          setIsCheckingSpareDevices(false);
        })
        .catch(error => {
          setSpareDeviceCheck({
            canAdd: false,
            message: 'Error checking device availability'
          });
          setIsCheckingSpareDevices(false);
        });
    }
  }, [label, modalFormDevices, orderData, formFields]);
  
  const modalForm =
    modalForms &&
    modalForms.length &&
    modalForms.find(item => item.fields.name === label);
  const modalFormFields =
    modalForm && modalForm.fields && modalForm.fields.formStepFields;
  const key = label === modalFormDevices ? keyDevices : keySpareDevices;
  const devices = formFields[key];
  const businessAppPlans = formFields[keyBusinessAppPlans] || [];
  
  // Check device limits (only for non-BA orders)
  const isPrimaryDevices = label === modalFormDevices;
  const isBAOrder = orderData && orderData.type === 'BA';
  
  let limitCheck = { isLimitReached: false, canAdd: true, message: '' };
  if (!isBAOrder) {
    limitCheck = isPrimaryDevices
      ? checkPrimaryDeviceLimit(formFields, orderData)
      : spareDeviceCheck || checkSpareDeviceLimit(formFields, orderData);
  }
  
  const isButtonDisabled = isBAOrder ? readonly : (readonly || (isPrimaryDevices ? limitCheck.isLimitReached : (isCheckingSpareDevices || !limitCheck.canAdd)));
  const onDelete = index => {
    // Update dueNow and dueMonthly
    const item = devices[index];
    if (item.payForDevice === valuePaymentUpfront)
      setDueNow(dueNow - item.oneTimeCharges * item.quantity);
    else setDueMonthly(dueMonthly - item.monthlyCharges * item.quantity);
    if (
      formFields[fieldAvailableDiscountsAndPromotions] &&
      formFields[fieldAvailableDiscountsAndPromotions].value &&
      formFields[fieldAvailableDiscountsAndPromotions].value.length > 0 &&
      item.payForDevice === valuePaymentUpfront
    ) {
      formFields[fieldAvailableDiscountsAndPromotions].value = [];
    } 
    // Update form fields
    devices.splice(index, 1);
    setFormFields({
      ...formFields,
      [key]: devices,
      [keyDevicesWereChanged]: true
    });
    setIsFormTouched(true);
    setIsFormValidated(false);
  };

  const onBusinessPlanDelete = index => {
    const item = businessAppPlans[index];
    if (item.payForDevice === valuePaymentUpfront)
      setDueNow(dueNow - item.oneTimeCharges * item.quantity);
    else setDueMonthly(dueMonthly - item.monthlyCharges * item.quantity);

    // Update form fields
    businessAppPlans.splice(index, 1);
    setFormFields({
      ...formFields,
      [businessAppPlans]: businessAppPlans,
    });
    setIsFormTouched(true);
    setIsFormValidated(false);
  };

  //This logic is used to remove monthly price for addSpareDevices
  const clonedFields =
    name === 'addSpareDevices'
      ? (() => {
          const copy = cloneDeep(modalFormFields);
          copy[0].fields.options.forEach(option => {
            option.fields.monthlyPayment = 0;
          });
          return copy;
        })()
      : modalFormFields;

  return (
    <FieldWrapper>
      {devices && devices.length > 0 && (
        <DevicesTable
          devices={devices}
          onDelete={isOrderFulfillmentTeam ? () => {} : onDelete}
        />
      )}
      {businessAppPlans && businessAppPlans.length > 0 && (
        <BusinessPlansTable
          businessAppPlans={businessAppPlans}
          onDelete={onBusinessPlanDelete}
          isDeletable
        />
      )}
      <div style={{ marginTop: '10px' }}>
        <Button
          type="button"
          name={name}
          variant={{ priority: 'high' }}
          onPress={() => {
            if (!isButtonDisabled) {
              setIsModalOpened(true);
            }
          }}
          disabled={isButtonDisabled}
        >
          {!isPrimaryDevices && isCheckingSpareDevices ? 'Checking...' : label}
        </Button>
        {((isPrimaryDevices && limitCheck.isLimitReached) || (!isPrimaryDevices && !limitCheck.canAdd)) && (
          <ErrorMessage type="text" text={limitCheck.message} />
        )}
      </div>
      <ModalForm
        fields={clonedFields}
        formFields={formFields}
        setFormFields={setFormFields}
        dueNow={dueNow}
        setDueNow={setDueNow}
        dueMonthly={dueMonthly}
        setDueMonthly={setDueMonthly}
        open={isModalOpened}
        header={label}
        onClose={() => setIsModalOpened(false)}
        setIsFormTouched={setIsFormTouched}
        setIsFormValidated={setIsFormValidated}
        isDevicesPromo={isDevicesPromo}
        orderData={orderData}
      />
    </FieldWrapper>
  );
};

FieldButton.propTypes = {
  data: PropTypes.shape({
    name: PropTypes.string.isRequired,
    label: PropTypes.string,
    readonly: PropTypes.bool,
    formFields: PropTypes.objectOf(PropTypes.any),
    setFormFields: PropTypes.func,
    modalForms: PropTypes.arrayOf(PropTypes.object).isRequired,
    dueNow: PropTypes.number.isRequired,
    setDueNow: PropTypes.func.isRequired,
    dueMonthly: PropTypes.number.isRequired,
    setDueMonthly: PropTypes.func.isRequired,
    setIsFormTouched: PropTypes.func.isRequired,
    setIsFormValidated: PropTypes.func.isRequired,
    isOrderFulfillmentTeam: PropTypes.bool.isRequired,
    isDevicesPromo: PropTypes.bool.isRequired
  }).isRequired,
  orderData: PropTypes.objectOf(PropTypes.any).isRequired
};

export default FieldButton;
